import React, { useState, useEffect } from 'react';
import LoadingSpinner from './components/LoadingSpinner';

export default function Podcast() {
  const [podcastData, setPodcastData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingEpisode, setPlayingEpisode] = useState(null);

  useEffect(() => {
    const fetchPodcast = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/podcast/episodes`);
        
        if (!response.ok) {
          throw new Error('Unable to load podcast episodes');
        }
        
        const data = await response.json();
        
        // Validate data structure
        if (!data || !Array.isArray(data.episodes)) {
          throw new Error('Unable to load podcast episodes');
        }
        
        setPodcastData(data);
        setError(null);
      } catch (err) {
        setError('Unable to load podcast episodes. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPodcast();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const truncateDescription = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <LoadingSpinner message="Loading podcast episodes..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '2rem 1rem',
        textAlign: 'center'
      }}>
        <h1 style={{ color: '#e74c3c', marginBottom: '1rem' }}>Error Loading Podcast</h1>
        <p style={{ color: '#666', marginBottom: '1rem' }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!podcastData || !podcastData.episodes || podcastData.episodes.length === 0) {
    return (
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '2rem 1rem',
        textAlign: 'center'
      }}>
        <p style={{ color: '#666' }}>No podcast episodes available.</p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '2rem 1rem'
    }}>
      {/* Podcast Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '3rem',
        paddingBottom: '2rem',
        borderBottom: '2px solid #e0e0e0'
      }}>
        {podcastData.image && (
          <img 
            src={podcastData.image} 
            alt={podcastData.title}
            style={{
              width: '200px',
              height: '200px',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
          />
        )}
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: '700',
          color: '#333',
          marginBottom: '0.5rem'
        }}>
          {podcastData.title}
        </h1>
        {podcastData.description && (
          <p style={{
            fontSize: '1.1rem',
            color: '#666',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            {podcastData.description}
          </p>
        )}
      </div>

      {/* Episodes List */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem'
      }}>
        {podcastData.episodes.map((episode, index) => (
          <article
            key={episode.guid || index}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '1.5rem',
              marginBottom: '1rem'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '0.75rem'
                }}>
                  {episode.episodeNumber && (
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#38b44a',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Episode {episode.episodeNumber}
                    </span>
                  )}
                  {episode.duration && (
                    <span style={{
                      fontSize: '0.875rem',
                      color: '#999'
                    }}>
                      {formatDuration(episode.duration)}
                    </span>
                  )}
                  {episode.pubDate && (
                    <span style={{
                      fontSize: '0.875rem',
                      color: '#999'
                    }}>
                      {formatDate(episode.pubDate)}
                    </span>
                  )}
                </div>
                
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '0.75rem',
                  lineHeight: '1.3'
                }}>
                  {episode.title}
                </h2>
                
                {episode.description && (
                  <p style={{
                    fontSize: '1rem',
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '1rem'
                  }}>
                    {truncateDescription(episode.description, 300)}
                  </p>
                )}
              </div>
            </div>

            {/* Audio Player */}
            {episode.audioUrl && (
              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1.5rem',
                borderTop: '1px solid #e0e0e0'
              }}>
                <audio
                  controls
                  src={episode.audioUrl}
                  style={{
                    width: '100%',
                    height: '40px'
                  }}
                  onPlay={() => setPlayingEpisode(episode.guid)}
                  onPause={() => setPlayingEpisode(null)}
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}

            {/* External Link */}
            {episode.link && (
              <div style={{
                marginTop: '1rem',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <a
                  href={episode.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: '0.875rem',
                    color: '#38b44a',
                    textDecoration: 'none',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  Listen on Podbean →
                </a>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

